import { CheckCircleIcon } from '@heroicons/react/24/outline'
import type { RecipeLedgerRow } from './RecipeLogStep'
import { toBatch } from '../utils'

interface EodIncident {
  id: number
  description: string
  severity: 'low' | 'medium' | 'high'
}

interface EodSession {
  id: number
  status: string
  general_notes: string | null
  notes_for_tomorrow: string | null
  incidents: EodIncident[]
  submitted_at?: string | null
}

interface SessionSummaryCardProps {
  session: EodSession
  recipes: RecipeLedgerRow[]
}

const SEVERITY_STYLES: Record<string, string> = {
  low: 'bg-blue-100 text-blue-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-red-100 text-red-800',
}

const fmtQty = (n: number, unit: string, outputQty?: number | null) => {
  if (n <= 0) return null
  const batchVal = toBatch(n, outputQty)
  const batch = batchVal !== null ? ` · ${batchVal} batch` : ''
  return `${n} ${unit}${batch}`
}

interface SectionProps {
  title: string
  children: React.ReactNode
}

const Section = ({ title, children }: SectionProps) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
    {children}
  </div>
)

interface RecipeListProps {
  recipes: RecipeLedgerRow[]
  quantityKey: keyof RecipeLedgerRow
  emptyLabel?: string
}

const RecipeList = ({ recipes, quantityKey, emptyLabel = 'Nothing logged' }: RecipeListProps) => {
  const filtered = recipes.filter((r) => (r[quantityKey] as number) > 0)
  if (filtered.length === 0) return <p className="text-sm text-gray-400">{emptyLabel}</p>
  return (
    <ul className="space-y-1.5">
      {filtered.map((r) => (
        <li key={r.raw_material_id} className="flex items-center justify-between text-sm">
          <span className="text-gray-700">{r.raw_material_name}</span>
          <span className="font-medium text-gray-900">{fmtQty(r[quantityKey] as number, r.unit, r.output_quantity)}</span>
        </li>
      ))}
    </ul>
  )
}

const SessionSummaryCard = ({ session, recipes }: SessionSummaryCardProps) => {
  const submittedAt = session.submitted_at
    ? new Date(session.submitted_at).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })
    : null

  return (
    <div className="space-y-4">
      {/* Submitted banner */}
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        <CheckCircleIcon className="h-5 w-5 shrink-0" />
        <span>
          <span className="font-semibold">Session submitted</span>
          {submittedAt && <span className="ml-1 text-green-700">— {submittedAt}</span>}
        </span>
      </div>

      {/* Inventory blocks — 3-column grid on large screens */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Section title="Cooked">
          <RecipeList recipes={recipes} quantityKey="cooked_quantity" />
        </Section>

        <Section title="Reheated">
          <RecipeList recipes={recipes} quantityKey="reheated_quantity" />
        </Section>

        <Section title="Sold via POS">
          <RecipeList recipes={recipes} quantityKey="sold_quantity" />
        </Section>

        <Section title="Stored in fridge">
          <RecipeList recipes={recipes} quantityKey="stored_quantity" />
        </Section>

        <Section title="Wasted">
          <RecipeList recipes={recipes} quantityKey="wasted_quantity" />
        </Section>

        <Section title="Expired">
          <RecipeList recipes={recipes} quantityKey="expired_quantity" />
        </Section>

        <Section title="Staff consumption">
          <RecipeList recipes={recipes} quantityKey="consumed_staff_quantity" />
        </Section>

        {/* Variance summary */}
        <Section title="Variance">
          {recipes.every((r) => r.variance_quantity === 0) ? (
            <p className="text-sm text-green-700 font-medium">All balanced ✓</p>
          ) : (
            <ul className="space-y-1.5">
              {recipes.filter((r) => r.variance_quantity !== 0).map((r) => (
                <li key={r.raw_material_id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{r.raw_material_name}</span>
                  <span className={r.variance_quantity < 0 ? 'text-red-700 font-semibold' : 'text-yellow-700'}>
                    {r.variance_quantity > 0 ? '+' : ''}{r.variance_quantity} {r.unit}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* Incidents + notes */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Section title="Incidents">
          {session.incidents.length === 0 ? (
            <p className="text-sm text-gray-400">No incidents</p>
          ) : (
            <ul className="space-y-2">
              {session.incidents.map((inc) => (
                <li key={inc.id} className="flex items-start gap-2">
                  <span className={`mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${SEVERITY_STYLES[inc.severity]}`}>
                    {inc.severity}
                  </span>
                  <p className="text-sm text-gray-800">{inc.description}</p>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Notes">
          {!session.general_notes && !session.notes_for_tomorrow ? (
            <p className="text-sm text-gray-400">No notes</p>
          ) : (
            <div className="space-y-3 text-sm">
              {session.general_notes && (
                <div>
                  <p className="font-medium text-gray-600 mb-0.5">General</p>
                  <p className="text-gray-800 whitespace-pre-wrap">{session.general_notes}</p>
                </div>
              )}
              {session.notes_for_tomorrow && (
                <div>
                  <p className="font-medium text-gray-600 mb-0.5">For tomorrow</p>
                  <p className="text-gray-800 whitespace-pre-wrap">{session.notes_for_tomorrow}</p>
                </div>
              )}
            </div>
          )}
        </Section>
      </div>
    </div>
  )
}

export default SessionSummaryCard
